// src/sampleTour.js
// Creates a fully-populated sample tour for onboarding / testing.
// Returns { success, inviteLinks, claimLinks, error }

import { supabase } from "./supabase.js";

const uid = () => crypto.randomUUID();

export async function createSampleTour(teamId, userId) {
  try {
    // ── Date helpers ──────────────────────────────────────────────────────────
    // Tour is structured so today = day 14 (off day in Denver, mid-tour).
    // d(n) returns a YYYY-MM-DD string for tour day n (0 = tour start, 14 = today).
    const _start = new Date();
    _start.setHours(12, 0, 0, 0);
    _start.setDate(_start.getDate() - 14); // tour started 14 days ago
    const d = (n) => {
      const dt = new Date(_start);
      dt.setDate(dt.getDate() + n);
      return dt.toISOString().slice(0, 10);
    };
    const dt = (n, time) => `${d(n)}T${time}`;
    const year = new Date().getFullYear().toString();

    // ── IDs ───────────────────────────────────────────────────────────────────
    const artistId = uid();
    const tourId   = uid();

    const vDC   = uid(); // 9:30 Club
    const vCam  = uid(); // The Sinclair
    const vBow  = uid(); // Bowery Ballroom
    const vTha  = uid(); // Thalia Hall
    const vOgd  = uid(); // Ogden Theatre
    const vWil  = uid(); // The Wiltern
    const vFil  = uid(); // The Fillmore
    const vRick = uid(); // Rickshaw Stop

    const sDC    = uid(); // d(1) show
    const sCam   = uid(); // d(3) show
    const sBow1  = uid(); // d(5) show
    const sBow2  = uid(); // d(7) show
    const sChi   = uid(); // d(10) show
    const sDen   = uid(); // d(15) show
    const sLA    = uid(); // d(18) show
    const sSF1   = uid(); // d(21) show
    const sSF2   = uid(); // d(22) show

    // Tour day IDs (24 days: d(0)–d(23))
    const td = Array.from({ length: 24 }, uid);

    // ── 1. Artist ─────────────────────────────────────────────────────────────
    const { data: artData, error: artErr } = await supabase.from("artists").insert({
      id: artistId, user_id: userId, team_id: teamId,
      name: "The Meridian",
      mgmt: { name: "LR Artists", email: "Martin@lrartists.com", phone: "+1 212.555.0100" },
      booking: { name: "Martin Earley", email: "Martin@lrartists.com", phone: "+1 212.555.0100", company: "LR Artists" },
      label: { name: "Meridian Records", contact: "A&R", email: "ar@meridianrecords.fake" },
      members: ["Alex Rivera", "Jordan Kim", "Sam Torres"],
      website: "https://themeridian.fake",
      socials: { instagram: "@themeridian", twitter: "@themeridian" },
      rider: "Hospitality: 1 case IPA, 1 case still water, hot meal for 6 pre-show.\nTechnical: Full backline provided by venue unless noted.",
    }).select("id");
    if (artErr) throw new Error(`artist: ${artErr.message}`);
    if (!artData?.length) throw new Error(`artist: insert returned no rows (possible RLS block)`);

    // ── 2. Venues ─────────────────────────────────────────────────────────────
    const venues = [
      {
        id: vDC, user_id: userId, team_id: teamId, kind: "venue",
        name: "9:30 Club", city: "Washington", address_state: "DC",
        address_street: "815 V St NW", address_zip: "20001", address_country: "US",
        capacity: "1200", ages: "18+", ga_seated: "GA",
        website: "https://www.930.com",
        promoter_name: "I.M.P.", promoter_email: "booking@930.com", promoter_company: "I.M.P.",
        promoter_phone: "+1 202.265.0930",
        load_in: "3:00 PM", house_gear: "Full backline, L-Acoustics PA",
        parking: "Street parking + lot on 9th St NW",
        notes: "Load in via alley on 9th. Security clears by 5pm.",
      },
      {
        id: vCam, user_id: userId, team_id: teamId, kind: "venue",
        name: "The Sinclair", city: "Cambridge", address_state: "MA",
        address_street: "52 Church St", address_zip: "02138", address_country: "US",
        capacity: "525", ages: "18+", ga_seated: "GA",
        website: "https://www.sinclaircambridge.com",
        promoter_name: "Boston Calling Events", promoter_email: "info@sinclaircambridge.com",
        promoter_phone: "+1 617.547.5200",
        load_in: "4:00 PM", house_gear: "d&b audiotechnik PA, full backline",
        parking: "Harvard Square garage off JFK St",
      },
      {
        id: vBow, user_id: userId, team_id: teamId, kind: "venue",
        name: "Bowery Ballroom", city: "New York", address_state: "NY",
        address_street: "6 Delancey St", address_zip: "10002", address_country: "US",
        capacity: "575", ages: "21+", ga_seated: "GA",
        website: "https://www.boweryballroom.com",
        promoter_name: "Bowery Presents", promoter_email: "info@bowerypresents.com",
        promoter_phone: "+1 212.533.2111",
        load_in: "3:30 PM", house_gear: "Martin Audio PA, full backline",
        parking: "Metered street or Delancey/Essex garage",
        merch_split: "80/20",
        notes: "Two nights. Load in via Delancey. Catering in green room upstairs.",
      },
      {
        id: vTha, user_id: userId, team_id: teamId, kind: "venue",
        name: "Thalia Hall", city: "Chicago", address_state: "IL",
        address_street: "1807 S Allport St", address_zip: "60608", address_country: "US",
        capacity: "777", ages: "17+", ga_seated: "GA/seated combo",
        website: "https://www.thaliahallchicago.com",
        promoter_name: "Jam Productions", promoter_email: "info@thaliahallchicago.com",
        promoter_phone: "+1 312.526.3851",
        load_in: "2:00 PM", house_gear: "Meyer Sound PA",
        parking: "Lot adjacent on 18th St",
      },
      {
        id: vOgd, user_id: userId, team_id: teamId, kind: "venue",
        name: "Ogden Theatre", city: "Denver", address_state: "CO",
        address_street: "935 E Colfax Ave", address_zip: "80218", address_country: "US",
        capacity: "1600", ages: "All Ages", ga_seated: "GA",
        website: "https://www.ogdentheatre.com",
        promoter_name: "AEG Presents Rocky Mountains", promoter_email: "advancing@ogdentheatre.com",
        promoter_phone: "+1 303.832.1874",
        load_in: "2:30 PM", house_gear: "L-Acoustics KARA system",
        notes: "Altitude: 5,280ft. Band may need extra warm-up time.",
      },
      {
        id: vWil, user_id: userId, team_id: teamId, kind: "venue",
        name: "The Wiltern", city: "Los Angeles", address_state: "CA",
        address_street: "3790 Wilshire Blvd", address_zip: "90010", address_country: "US",
        capacity: "1850", ages: "All Ages", ga_seated: "GA",
        website: "https://www.wiltern.com",
        promoter_name: "Live Nation", promoter_email: "production@wiltern.com",
        promoter_phone: "+1 213.388.1400",
        load_in: "1:00 PM", house_gear: "EAW rig, full backline available",
        parking: "Parking garage below venue (entrance on Wilshire)",
        merch_split: "85/15",
      },
      {
        id: vFil, user_id: userId, team_id: teamId, kind: "venue",
        name: "The Fillmore", city: "San Francisco", address_state: "CA",
        address_street: "1805 Geary Blvd", address_zip: "94115", address_country: "US",
        capacity: "1150", ages: "All Ages", ga_seated: "GA",
        website: "https://www.thefillmore.com",
        promoter_name: "Live Nation", promoter_email: "advancing@thefillmore.com",
        promoter_phone: "+1 415.346.6000",
        load_in: "2:00 PM", house_gear: "Meyer Sound PA",
        notes: "Famous poster wall in lobby — good for promo photos. Catering in upper green room.",
        merch_split: "85/15",
      },
      {
        id: vRick, user_id: userId, team_id: teamId, kind: "venue",
        name: "Rickshaw Stop", city: "San Francisco", address_state: "CA",
        address_street: "155 Fell St", address_zip: "94102", address_country: "US",
        capacity: "400", ages: "All Ages", ga_seated: "GA",
        website: "https://www.rickshawstop.com",
        promoter_name: "Noise Pop", promoter_email: "info@rickshawstop.com",
        promoter_phone: "+1 415.861.2011",
        load_in: "5:00 PM", house_gear: "In-house PA",
        notes: "Intimate late-night show. Load in via Fell St side door.",
      },
    ];
    const { error: venErr } = await supabase.from("venues").insert(venues);
    if (venErr) throw new Error(`venues: ${venErr.message}`);

    // ── 3. Tour ───────────────────────────────────────────────────────────────
    const { data: tourData, error: tourErr } = await supabase.from("tours").insert({
      id: tourId, artist_id: artistId, user_id: userId, team_id: teamId,
      name: `Spring ${year} North America`,
      year,
      advance_config: {},
    }).select("id");
    if (tourErr) throw new Error(`tour: ${tourErr.message}`);
    if (!tourData?.length) throw new Error(`tour: insert returned no rows (possible RLS block)`);

    // ── 4. Shows ──────────────────────────────────────────────────────────────
    const showBase = { tour_id: tourId, user_id: userId, team_id: teamId,
      notes: "", flagged: false, advance_token: null, lodging: {}, linked_contacts: [] };
    const shows = [
      {
        ...showBase, id: sDC, venue_id: vDC, date: d(1), status: "confirmed",
        advance_data: {
          promoterName: "I.M.P.", promoterPhone: "+1 202.265.0930",
          loadIn: "3:00 PM", doors: "7:00 PM", showTime: "8:30 PM", setLength: "75 min",
          hospitalityNotes: "Hot meal for 6 @ 5pm. IPA on rider.",
          productionNotes: "Marcus Webb advancing FOH. Venue BM is Jake Torres.",
        },
        advance_tm_data: { notes: "Great room, sold out in advance. Get there by 2:30 for parking." },
        checklist: { contract: true, deposit: true, rider: true, advance: true, settlement: false },
        settlement: { guarantee: 8000, expenses: 1200, net: 6800 },
      },
      {
        ...showBase, id: sCam, venue_id: vCam, date: d(3), status: "confirmed",
        advance_data: {
          promoterName: "Boston Calling Events", promoterPhone: "+1 617.547.5200",
          loadIn: "4:00 PM", doors: "7:30 PM", showTime: "9:00 PM", setLength: "70 min",
          hospitalityNotes: "Dinner from Harvest restaurant pre-show.",
        },
        advance_tm_data: { notes: "Quick turnaround from DC. Drive is ~7hrs." },
        checklist: { contract: true, deposit: true, rider: true, advance: true, settlement: false },
        settlement: { guarantee: 5500, expenses: 900, net: 4600 },
      },
      {
        ...showBase, id: sBow1, venue_id: vBow, date: d(5), status: "confirmed",
        advance_data: {
          promoterName: "Bowery Presents", promoterPhone: "+1 212.533.2111",
          loadIn: "3:30 PM", doors: "7:00 PM", showTime: "8:30 PM", setLength: "75 min",
          hospitalityNotes: "Catering in upstairs green room. Mexican food request OK'd.",
        },
        advance_tm_data: { notes: "Night 1 of 2. Check in hotel first, then head to venue." },
        checklist: { contract: true, deposit: true, rider: true, advance: true, settlement: false },
        settlement: { guarantee: 9000, expenses: 800, net: 8200 },
      },
      {
        ...showBase, id: sBow2, venue_id: vBow, date: d(7), status: "confirmed",
        advance_data: {
          promoterName: "Bowery Presents", promoterPhone: "+1 212.533.2111",
          loadIn: "3:30 PM", doors: "7:00 PM", showTime: "8:30 PM", setLength: "80 min",
          hospitalityNotes: "Same rider as night 1.",
        },
        advance_tm_data: { notes: "Night 2. Alex's family will be at this show — guest list +4." },
        checklist: { contract: true, deposit: true, rider: true, advance: true, settlement: false },
        settlement: { guarantee: 9000, expenses: 750, net: 8250 },
      },
      {
        ...showBase, id: sChi, venue_id: vTha, date: d(10), status: "confirmed",
        advance_data: {
          promoterName: "Jam Productions", promoterPhone: "+1 312.526.3851",
          loadIn: "2:00 PM", doors: "6:30 PM", showTime: "8:00 PM", setLength: "75 min",
          hospitalityNotes: "Deep-dish pizza + salads for 8.",
        },
        advance_tm_data: { notes: "Venue is gorgeous. Great merch spot near the bar." },
        checklist: { contract: true, deposit: true, rider: true, advance: true, settlement: false },
        settlement: { guarantee: 7500, expenses: 950, net: 6550 },
      },
      {
        ...showBase, id: sDen, venue_id: vOgd, date: d(15), status: "advancing",
        advance_data: {
          promoterName: "AEG Presents Rocky Mountains", promoterPhone: "+1 303.832.1874",
          loadIn: "2:30 PM", doors: "7:00 PM", showTime: "8:30 PM",
        },
        advance_tm_data: { notes: "Remember altitude — band may need to warm up longer." },
        checklist: { contract: true, deposit: true, rider: false, advance: false },
        settlement: {},
      },
      {
        ...showBase, id: sLA, venue_id: vWil, date: d(18), status: "advancing",
        advance_data: { promoterName: "Live Nation", promoterPhone: "+1 213.388.1400", loadIn: "1:00 PM" },
        advance_tm_data: {},
        checklist: { contract: true, deposit: false, rider: false, advance: false },
        settlement: {},
      },
      {
        ...showBase, id: sSF1, venue_id: vFil, date: d(21), status: "advancing",
        advance_data: { promoterName: "Live Nation", promoterPhone: "+1 415.346.6000", loadIn: "2:00 PM" },
        advance_tm_data: {},
        checklist: { contract: true, deposit: false, rider: false, advance: false },
        settlement: {},
      },
      {
        ...showBase, id: sSF2, venue_id: vRick, date: d(22), status: "unstarted",
        advance_data: {}, advance_tm_data: {}, checklist: {}, settlement: {},
      },
    ];
    const { error: showErr } = await supabase.from("shows").insert(shows);
    if (showErr) throw new Error(`shows: ${showErr.message}`);

    // ── 5. Tour Days ──────────────────────────────────────────────────────────
    // 24 days. d(0) = tour start, d(14) = TODAY (off day in Denver).
    const tourDays = [
      // d(0) — travel to DC
      { id: td[0], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(0), type: "travel", city: "Washington", country: "US",
        bus_call: "5:00 AM",
        notes: "Fly JFK→DCA. Van picks up at JFK T4 at 5:00 AM.",
        hotel_name: "Kimpton George Hotel", hotel_address: "15 E St NW, Washington, DC 20001",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "KGH-DC-001", hotel_phone: "+1 202.347.4200", hotel_contact: "" },

      // d(1) — show: 9:30 Club, DC
      { id: td[1], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(1), type: "show", city: "Washington", country: "US",
        show_id: sDC, bus_call: "1:00 PM",
        notes: "Doors 7 / Show 8:30. Sold out.",
        hotel_name: "Kimpton George Hotel", hotel_address: "15 E St NW, Washington, DC 20001",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "KGH-DC-001", hotel_phone: "+1 202.347.4200", hotel_contact: "" },

      // d(2) — travel DC→Cambridge
      { id: td[2], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(2), type: "travel", city: "Cambridge", country: "US",
        bus_call: "10:00 AM",
        notes: "Drive DC→Cambridge (~7 hrs). Arrive hotel ~6 PM.",
        hotel_name: "Hotel Veritas", hotel_address: "1 Remington St, Cambridge, MA 02138",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "HV-CAM-002", hotel_phone: "+1 617.520.5000", hotel_contact: "" },

      // d(3) — show: The Sinclair, Cambridge
      { id: td[3], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(3), type: "show", city: "Cambridge", country: "US",
        show_id: sCam, bus_call: "2:30 PM",
        notes: "Doors 7:30 / Show 9:00.",
        hotel_name: "Hotel Veritas", hotel_address: "1 Remington St, Cambridge, MA 02138",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "HV-CAM-002", hotel_phone: "+1 617.520.5000", hotel_contact: "" },

      // d(4) — travel Cambridge→NYC
      { id: td[4], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(4), type: "travel", city: "New York", country: "US",
        bus_call: "11:00 AM",
        notes: "Drive Boston→NYC (~4 hrs). NYC check-in by 4 PM.",
        hotel_name: "The Bowery Hotel", hotel_address: "335 Bowery, New York, NY 10003",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "TBH-NYC-003", hotel_phone: "+1 212.505.9100", hotel_contact: "" },

      // d(5) — show: Bowery Ballroom night 1
      { id: td[5], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(5), type: "show", city: "New York", country: "US",
        show_id: sBow1, bus_call: "2:00 PM",
        notes: "Night 1 of 2. Doors 7 / Show 8:30.",
        hotel_name: "The Bowery Hotel", hotel_address: "335 Bowery, New York, NY 10003",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "TBH-NYC-003", hotel_phone: "+1 212.505.9100", hotel_contact: "" },

      // d(6) — off day NYC
      { id: td[6], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(6), type: "off", city: "New York", country: "US",
        bus_call: "", notes: "Day off. Press day — interviews 11 AM–2 PM at hotel lobby.",
        hotel_name: "The Bowery Hotel", hotel_address: "335 Bowery, New York, NY 10003",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "TBH-NYC-003", hotel_phone: "+1 212.505.9100", hotel_contact: "" },

      // d(7) — show: Bowery Ballroom night 2
      { id: td[7], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(7), type: "show", city: "New York", country: "US",
        show_id: sBow2, bus_call: "2:00 PM",
        notes: "Night 2. Alex's family on guest list +4.",
        hotel_name: "The Bowery Hotel", hotel_address: "335 Bowery, New York, NY 10003",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "TBH-NYC-003", hotel_phone: "+1 212.505.9100", hotel_contact: "" },

      // d(8) — travel NYC→Chicago
      { id: td[8], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(8), type: "travel", city: "Chicago", country: "US",
        bus_call: "6:00 AM",
        notes: "Fly JFK→ORD. Van to JFK 6 AM sharp.",
        hotel_name: "Ace Hotel Chicago", hotel_address: "311 N Morgan St, Chicago, IL 60607",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "ACH-CHI-004", hotel_phone: "+1 312.764.1919", hotel_contact: "" },

      // d(9) — off day Chicago
      { id: td[9], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(9), type: "off", city: "Chicago", country: "US",
        bus_call: "", notes: "Day off in Chicago. Band day — no call time.",
        hotel_name: "Ace Hotel Chicago", hotel_address: "311 N Morgan St, Chicago, IL 60607",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "ACH-CHI-004", hotel_phone: "+1 312.764.1919", hotel_contact: "" },

      // d(10) — show: Thalia Hall, Chicago
      { id: td[10], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(10), type: "show", city: "Chicago", country: "US",
        show_id: sChi, bus_call: "12:00 PM",
        notes: "Doors 6:30 / Show 8:00. Deep dish ordered for load-in.",
        hotel_name: "Ace Hotel Chicago", hotel_address: "311 N Morgan St, Chicago, IL 60607",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "ACH-CHI-004", hotel_phone: "+1 312.764.1919", hotel_contact: "" },

      // d(11) — travel Chicago→Denver
      { id: td[11], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(11), type: "travel", city: "Denver", country: "US",
        bus_call: "7:00 AM",
        notes: "Fly ORD→DEN. Van at 7 AM from hotel.",
        hotel_name: "The Crawford Hotel", hotel_address: "1701 Wynkoop St, Denver, CO 80202",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "TCH-DEN-005", hotel_phone: "+1 720.460.3700", hotel_contact: "" },

      // d(12) — off Denver
      { id: td[12], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(12), type: "off", city: "Denver", country: "US",
        bus_call: "", notes: "Day off. RiNo Art District nearby — good food + coffee.",
        hotel_name: "The Crawford Hotel", hotel_address: "1701 Wynkoop St, Denver, CO 80202",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "TCH-DEN-005", hotel_phone: "+1 720.460.3700", hotel_contact: "" },

      // d(13) — off Denver
      { id: td[13], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(13), type: "off", city: "Denver", country: "US",
        bus_call: "", notes: "Day off. Sound check / production rehearsal at Ogden 3–5 PM if needed.",
        hotel_name: "The Crawford Hotel", hotel_address: "1701 Wynkoop St, Denver, CO 80202",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "TCH-DEN-005", hotel_phone: "+1 720.460.3700", hotel_contact: "" },

      // d(14) — off Denver (TODAY)
      { id: td[14], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(14), type: "off", city: "Denver", country: "US",
        bus_call: "", notes: "Day off. Show advance calls this afternoon.",
        hotel_name: "The Crawford Hotel", hotel_address: "1701 Wynkoop St, Denver, CO 80202",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "TCH-DEN-005", hotel_phone: "+1 720.460.3700", hotel_contact: "" },

      // d(15) — show: Ogden Theatre, Denver
      { id: td[15], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(15), type: "show", city: "Denver", country: "US",
        show_id: sDen, bus_call: "12:30 PM",
        notes: "Doors 7:00 / Show 8:30. Remember altitude — extra warm-up.",
        hotel_name: "The Crawford Hotel", hotel_address: "1701 Wynkoop St, Denver, CO 80202",
        hotel_check_in: "3:00 PM", hotel_check_out: "11:00 AM",
        hotel_confirmation: "TCH-DEN-005", hotel_phone: "+1 720.460.3700", hotel_contact: "" },

      // d(16) — travel Denver→LA
      { id: td[16], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(16), type: "travel", city: "Los Angeles", country: "US",
        bus_call: "8:00 AM",
        notes: "Fly DEN→LAX. Van at 8 AM.",
        hotel_name: "Ace Hotel Downtown LA", hotel_address: "929 S Broadway, Los Angeles, CA 90015",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "ACDLA-LA-006", hotel_phone: "+1 213.623.3233", hotel_contact: "" },

      // d(17) — off LA
      { id: td[17], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(17), type: "off", city: "Los Angeles", country: "US",
        bus_call: "", notes: "Day off in LA. Possible radio promo TBC.",
        hotel_name: "Ace Hotel Downtown LA", hotel_address: "929 S Broadway, Los Angeles, CA 90015",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "ACDLA-LA-006", hotel_phone: "+1 213.623.3233", hotel_contact: "" },

      // d(18) — show: The Wiltern, LA
      { id: td[18], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(18), type: "show", city: "Los Angeles", country: "US",
        show_id: sLA, bus_call: "11:00 AM",
        notes: "Doors 7:00 / Show 8:00. Big room — bring energy.",
        hotel_name: "Ace Hotel Downtown LA", hotel_address: "929 S Broadway, Los Angeles, CA 90015",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "ACDLA-LA-006", hotel_phone: "+1 213.623.3233", hotel_contact: "" },

      // d(19) — travel LA→SF (drive)
      { id: td[19], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(19), type: "travel", city: "San Francisco", country: "US",
        bus_call: "10:00 AM",
        notes: "Drive LA→SF via PCH (~6 hrs). Scenic route approved.",
        hotel_name: "Hotel Zephyr San Francisco", hotel_address: "250 Beach St, San Francisco, CA 94133",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "HZ-SF-007", hotel_phone: "+1 415.617.6565", hotel_contact: "" },

      // d(20) — off SF
      { id: td[20], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(20), type: "off", city: "San Francisco", country: "US",
        bus_call: "", notes: "Day off. Mission District recommended for food.",
        hotel_name: "Hotel Zephyr San Francisco", hotel_address: "250 Beach St, San Francisco, CA 94133",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "HZ-SF-007", hotel_phone: "+1 415.617.6565", hotel_contact: "" },

      // d(21) — show: The Fillmore, SF
      { id: td[21], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(21), type: "show", city: "San Francisco", country: "US",
        show_id: sSF1, bus_call: "12:00 PM",
        notes: "Doors 7:00 / Show 8:30. Free apples at Fillmore — band tradition.",
        hotel_name: "Hotel Zephyr San Francisco", hotel_address: "250 Beach St, San Francisco, CA 94133",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "HZ-SF-007", hotel_phone: "+1 415.617.6565", hotel_contact: "" },

      // d(22) — show: Rickshaw Stop, SF (intimate late show)
      { id: td[22], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(22), type: "show", city: "San Francisco", country: "US",
        show_id: sSF2, bus_call: "4:00 PM",
        notes: "Late intimate show. Doors 9 PM / Show 10 PM.",
        hotel_name: "Hotel Zephyr San Francisco", hotel_address: "250 Beach St, San Francisco, CA 94133",
        hotel_check_in: "3:00 PM", hotel_check_out: "12:00 PM",
        hotel_confirmation: "HZ-SF-007", hotel_phone: "+1 415.617.6565", hotel_contact: "" },

      // d(23) — travel SF→home (fly SFO→JFK)
      { id: td[23], tour_id: tourId, user_id: userId, team_id: teamId,
        date: d(23), type: "travel", city: "New York", country: "US",
        bus_call: "7:00 AM",
        notes: "Tour wrap. Fly SFO→JFK. Van at 7 AM sharp. Safe travels everyone! 🎸",
        hotel_name: "", hotel_address: "", hotel_check_in: "", hotel_check_out: "",
        hotel_confirmation: "", hotel_phone: "", hotel_contact: "",
      },
    ];
    const { data: tdData, error: tdErr } = await supabase.from("tour_days").insert(tourDays).select("id, date");
    if (tdErr) throw new Error(`tour_days: ${tdErr.message}`);
    if (!tdData?.length) throw new Error(`tour_days: insert returned no rows (possible RLS block)`);

    // Build date → actual DB id map (DB may generate its own IDs ignoring our td[] values)
    const dayId = {};
    tdData.forEach(r => { dayId[String(r.date).slice(0, 10)] = r.id; });

    // ── 6. Lodging ────────────────────────────────────────────────────────────
    const lodging = [
      { tour_day_id: dayId[d(0)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "Kimpton George Hotel",
        address: "15 E St NW, Washington, DC 20001",
        chain: "Kimpton", check_in: "3:00 PM", check_out: "11:00 AM",
        confirmation: "KGH-DC-001", phone: "+1 202.347.4200",
        contact: "Front Desk", rooms: "4 rooms (2 double, 2 king)",
        notes: "DC show nights. Walking distance to 9:30 Club." },

      { tour_day_id: dayId[d(2)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "Hotel Veritas",
        address: "1 Remington St, Cambridge, MA 02138",
        chain: "", check_in: "3:00 PM", check_out: "11:00 AM",
        confirmation: "HV-CAM-002", phone: "+1 617.520.5000",
        contact: "Reservations", rooms: "4 rooms",
        notes: "Cambridge nights. Harvard Square location." },

      { tour_day_id: dayId[d(4)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "The Bowery Hotel",
        address: "335 Bowery, New York, NY 10003",
        chain: "", check_in: "3:00 PM", check_out: "12:00 PM",
        confirmation: "TBH-NYC-003", phone: "+1 212.505.9100",
        contact: "Concierge", rooms: "5 rooms (4 king, 1 suite)",
        notes: "NYC stay — 4 nights. Near Bowery Ballroom. Suite for production office." },

      { tour_day_id: dayId[d(8)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "Ace Hotel Chicago",
        address: "311 N Morgan St, Chicago, IL 60607",
        chain: "Ace", check_in: "3:00 PM", check_out: "11:00 AM",
        confirmation: "ACH-CHI-004", phone: "+1 312.764.1919",
        contact: "Front Desk", rooms: "4 rooms",
        notes: "Chicago stay — 3 nights. West Loop / Fulton Market location." },

      { tour_day_id: dayId[d(11)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "The Crawford Hotel",
        address: "1701 Wynkoop St, Denver, CO 80202",
        chain: "", check_in: "3:00 PM", check_out: "11:00 AM",
        confirmation: "TCH-DEN-005", phone: "+1 720.460.3700",
        contact: "Reservations", rooms: "4 rooms",
        notes: "Denver stay — 5 nights including today. Union Station location. Long stretch — great spot." },

      { tour_day_id: dayId[d(16)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "Ace Hotel Downtown LA",
        address: "929 S Broadway, Los Angeles, CA 90015",
        chain: "Ace", check_in: "3:00 PM", check_out: "12:00 PM",
        confirmation: "ACDLA-LA-006", phone: "+1 213.623.3233",
        contact: "Front Desk", rooms: "4 rooms",
        notes: "LA stay — 3 nights. DTLA location, close to Wiltern." },

      { tour_day_id: dayId[d(19)], tour_id: tourId, user_id: userId, team_id: teamId,
        type: "hotel", name: "Hotel Zephyr San Francisco",
        address: "250 Beach St, San Francisco, CA 94133",
        chain: "", check_in: "3:00 PM", check_out: "12:00 PM",
        confirmation: "HZ-SF-007", phone: "+1 415.617.6565",
        contact: "Concierge", rooms: "4 rooms",
        notes: "SF stay — 4 nights. Fisherman's Wharf area. Checkout day of wrap." },
    ];
    const { error: lodgErr } = await supabase.from("lodging").insert(lodging);
    if (lodgErr) throw new Error(`lodging: ${lodgErr.message}`);

    // ── 7. Flights ────────────────────────────────────────────────────────────
    const flights = [
      // d(0): JFK→DCA
      { tour_day_id: dayId[d(0)], user_id: userId, team_id: teamId,
        flight_number: "AA1234", airline: "American Airlines", airline_code: "AA",
        origin: "JFK", destination: "DCA",
        origin_city: "New York", destination_city: "Washington DC",
        scheduled_dep: dt(0, "07:30:00"), scheduled_arr: dt(0, "09:05:00"),
        cabin_class: "Economy", booking_ref: "XKJF7A", status: "confirmed",
        passenger_ids: [] },

      // d(8): JFK→ORD
      { tour_day_id: dayId[d(8)], user_id: userId, team_id: teamId,
        flight_number: "UA892", airline: "United Airlines", airline_code: "UA",
        origin: "JFK", destination: "ORD",
        origin_city: "New York", destination_city: "Chicago",
        scheduled_dep: dt(8, "09:00:00"), scheduled_arr: dt(8, "10:45:00"),
        cabin_class: "Economy", booking_ref: "NB7X2Q", status: "confirmed",
        passenger_ids: [] },

      // d(11): ORD→DEN
      { tour_day_id: dayId[d(11)], user_id: userId, team_id: teamId,
        flight_number: "UA456", airline: "United Airlines", airline_code: "UA",
        origin: "ORD", destination: "DEN",
        origin_city: "Chicago", destination_city: "Denver",
        scheduled_dep: dt(11, "09:30:00"), scheduled_arr: dt(11, "11:15:00"),
        cabin_class: "Economy", booking_ref: "TW3PLR", status: "confirmed",
        passenger_ids: [] },

      // d(16): DEN→LAX
      { tour_day_id: dayId[d(16)], user_id: userId, team_id: teamId,
        flight_number: "SW2210", airline: "Southwest Airlines", airline_code: "WN",
        origin: "DEN", destination: "LAX",
        origin_city: "Denver", destination_city: "Los Angeles",
        scheduled_dep: dt(16, "10:45:00"), scheduled_arr: dt(16, "11:50:00"),
        cabin_class: "Economy", booking_ref: "QR9ABW", status: "confirmed",
        passenger_ids: [] },

      // d(23): SFO→JFK
      { tour_day_id: dayId[d(23)], user_id: userId, team_id: teamId,
        flight_number: "JB881", airline: "JetBlue", airline_code: "B6",
        origin: "SFO", destination: "JFK",
        origin_city: "San Francisco", destination_city: "New York",
        scheduled_dep: dt(23, "10:15:00"), scheduled_arr: dt(23, "18:45:00"),
        cabin_class: "Economy", booking_ref: "FP4NVZ", status: "confirmed",
        passenger_ids: [] },
    ];
    const { error: flErr } = await supabase.from("flights").insert(flights);
    if (flErr) throw new Error(`flights: ${flErr.message}`);

    // ── 8. Ground Transport ───────────────────────────────────────────────────
    const transport = [
      // d(2): Drive DC→Cambridge
      { tour_day_id: dayId[d(2)], user_id: userId, team_id: teamId,
        type: "van", pickup_time: "10:00 AM",
        pickup_location: "Kimpton George Hotel, 15 E St NW, Washington DC",
        dropoff_location: "Hotel Veritas, 1 Remington St, Cambridge MA",
        company: "Northeast Tour Vans", driver_name: "Pete Flanagan",
        driver_phone: "+1 617.555.0184", confirmation_number: "NTV-DC-CAM",
        notes: "7hr drive. Stop in New Haven ~2pm for lunch (recommend Frank Pepe's)." },

      // d(4): Drive Boston→NYC
      { tour_day_id: dayId[d(4)], user_id: userId, team_id: teamId,
        type: "van", pickup_time: "11:00 AM",
        pickup_location: "Hotel Veritas, 1 Remington St, Cambridge MA",
        dropoff_location: "The Bowery Hotel, 335 Bowery, New York NY",
        company: "Northeast Tour Vans", driver_name: "Pete Flanagan",
        driver_phone: "+1 617.555.0184", confirmation_number: "NTV-BOS-NYC",
        notes: "4hr drive. Cross GW Bridge, take FDR south to Delancey." },

      // d(19): Drive LA→SF
      { tour_day_id: dayId[d(19)], user_id: userId, team_id: teamId,
        type: "van", pickup_time: "10:00 AM",
        pickup_location: "Ace Hotel Downtown LA, 929 S Broadway, Los Angeles CA",
        dropoff_location: "Hotel Zephyr, 250 Beach St, San Francisco CA",
        company: "West Coast Tour Transport", driver_name: "Rosa Delgado",
        driver_phone: "+1 323.555.0211", confirmation_number: "WCTT-LA-SF",
        notes: "PCH scenic route approved. ~6 hrs. Stop in Morro Bay for lunch." },
    ];
    const { error: trErr } = await supabase.from("ground_transport").insert(transport);
    if (trErr) throw new Error(`ground_transport: ${trErr.message}`);

    // ── 9. Crew Members ───────────────────────────────────────────────────────
    const crewRows = [
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Alex Rivera", role: "Vocals / Guitar", travel_group: "band",
        email: "alex.rivera@themeridian.fake", phone: "+1 646.555.0121",
        notes: "Lead vocalist. Vegan. Room preference: king." },
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Jordan Kim", role: "Bass", travel_group: "band",
        email: "jordan.kim@themeridian.fake", phone: "+1 646.555.0122",
        notes: "Handles social media on the road." },
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Sam Torres", role: "Drums", travel_group: "band",
        email: "sam.torres@themeridian.fake", phone: "+1 646.555.0123",
        notes: "Earliest riser — always down for coffee run." },
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Sarah Chen", role: "Tour Manager", travel_group: "crew",
        email: "sarah.chen@lrartists.fake", phone: "+1 646.555.0124",
        notes: "TM. Has master itinerary. First point of contact for all venue questions." },
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Marcus Webb", role: "FOH Engineer", travel_group: "crew",
        email: "marcus.webb@foh.fake", phone: "+1 646.555.0125",
        notes: "Carries Waves USB dongle. Needs table + stool at FOH." },
      { tour_id: tourId, user_id: userId, team_id: teamId,
        name: "Priya Patel", role: "Merchandise", travel_group: "crew",
        email: "priya.patel@merch.fake", phone: "+1 646.555.0126",
        notes: "Also handles tour photography. Needs merch table near entrance." },
    ];
    const { data: crewData, error: crewErr } = await supabase
      .from("crew_members").insert(crewRows).select();
    if (crewErr) throw new Error(`crew_members: ${crewErr.message}`);

    // ── 10. People Profiles ───────────────────────────────────────────────────
    const profileTokens = {
      alex:   uid(), jordan: uid(), sam:    uid(),
      sarah:  uid(), marcus: uid(), priya:  uid(),
    };

    const profileRows = [
      { team_id: teamId, name: "Alex Rivera", role: "Vocals / Guitar",
        role_category: "band", email: "alex.rivera@themeridian.fake",
        phone: "+1 646.555.0121", city: "Brooklyn, NY",
        bio: "Lead vocalist and guitarist of The Meridian. On the road since 2019.",
        skills: ["Vocals", "Guitar", "Piano"],
        visible_to_network: true, invite_token: profileTokens.alex },
      { team_id: teamId, name: "Jordan Kim", role: "Bass",
        role_category: "band", email: "jordan.kim@themeridian.fake",
        phone: "+1 646.555.0122", city: "Brooklyn, NY",
        bio: "Bassist. Plays a vintage '72 Fender Jazz. Also produces under the name 'JK Remote'.",
        skills: ["Bass", "Production"],
        visible_to_network: true, invite_token: profileTokens.jordan },
      { team_id: teamId, name: "Sam Torres", role: "Drums",
        role_category: "band", email: "sam.torres@themeridian.fake",
        phone: "+1 646.555.0123", city: "Queens, NY",
        bio: "Drummer. DW kit, Evans heads. Session credits: 40+ records.",
        skills: ["Drums", "Percussion"],
        visible_to_network: true, invite_token: profileTokens.sam },
      { team_id: teamId, name: "Sarah Chen", role: "Tour Manager",
        role_category: "crew", email: "sarah.chen@lrartists.fake",
        phone: "+1 646.555.0124", city: "New York, NY",
        bio: "Tour manager with 8 years on the road. Former advance coordinator for Live Nation.",
        skills: ["Tour Management", "Advancing", "Logistics"],
        visible_to_network: false, invite_token: profileTokens.sarah },
      { team_id: teamId, name: "Marcus Webb", role: "FOH Engineer",
        role_category: "crew", email: "marcus.webb@foh.fake",
        phone: "+1 646.555.0125", city: "Nashville, TN",
        bio: "Front-of-house engineer. DiGiCo SD12 preferred. Touring since 2015.",
        skills: ["FOH", "DiGiCo", "Shure"],
        visible_to_network: true, invite_token: profileTokens.marcus },
      { team_id: teamId, name: "Priya Patel", role: "Merchandise",
        role_category: "crew", email: "priya.patel@merch.fake",
        phone: "+1 646.555.0126", city: "Los Angeles, CA",
        bio: "Merch manager and tour photographer. Specialises in indie and alternative acts.",
        skills: ["Merchandise", "Photography", "Social Media"],
        visible_to_network: true, invite_token: profileTokens.priya },
    ];
    const { error: profErr } = await supabase.from("people_profiles").insert(profileRows);
    if (profErr) throw new Error(`people_profiles: ${profErr.message}`);

    // ── 11. Tour Members (invite links) ───────────────────────────────────────
    const memberRows = [
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "alex.rivera@themeridian.fake", name: "Alex Rivera", role: "band" },
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "jordan.kim@themeridian.fake", name: "Jordan Kim", role: "band" },
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "sam.torres@themeridian.fake", name: "Sam Torres", role: "band" },
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "sarah.chen@lrartists.fake", name: "Sarah Chen", role: "crew" },
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "marcus.webb@foh.fake", name: "Marcus Webb", role: "crew" },
      { team_id: teamId, tour_id: tourId, artist_id: artistId,
        email: "priya.patel@merch.fake", name: "Priya Patel", role: "crew" },
    ];
    const { data: memberData, error: memErr } = await supabase
      .from("tour_members").insert(memberRows).select("invite_token, name, email, role");
    if (memErr) throw new Error(`tour_members: ${memErr.message}`);

    // ── Build return URLs ─────────────────────────────────────────────────────
    const origin = typeof window !== "undefined" ? window.location.origin : "";

    const inviteLinks = (memberData || []).map(m => ({
      name: m.name,
      role: m.role,
      email: m.email,
      url: `${origin}/invite/${m.invite_token}`,
    }));

    const claimLinks = Object.entries(profileTokens).map(([key, token]) => {
      const names = { alex: "Alex Rivera", jordan: "Jordan Kim", sam: "Sam Torres",
                      sarah: "Sarah Chen", marcus: "Marcus Webb", priya: "Priya Patel" };
      return { name: names[key], url: `${origin}/claim/${token}` };
    });

    return { success: true, inviteLinks, claimLinks, artistId, tourId };

  } catch (err) {
    console.error("[ADVANCD] createSampleTour failed:", err);
    return { success: false, error: err.message };
  }
}
